import React, { useState, useEffect, useCallback } from 'react';
import { useQueryStates, parseAsArrayOf, parseAsString } from 'nuqs';
import TreatmentSteps from './components/TreatmentSteps';
import TreatmentResults from './components/TreatmentResults';

const VIEWS = {
  STEPS: 'steps',
  RESULTS: 'results',
};

const URL_CONFIG = {
  history: 'push',
  shallow: false,
  throttleMs: 0,
  clearOnDefault: true,
};

const createUrlParsers = () => ({
  view: parseAsString.withDefault(VIEWS.STEPS),
  'treatment_cultures.culture': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_cultures.cultural_practices': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_type': parseAsArrayOf(parseAsString).withDefault([]),
  'application_type': parseAsArrayOf(parseAsString).withDefault([]),
  'intervention_period': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_cultures.culture_labels': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_cultures.cultural_practices_labels': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_type_labels': parseAsArrayOf(parseAsString).withDefault([]),
  'application_type_labels': parseAsArrayOf(parseAsString).withDefault([]),
  'intervention_period_labels': parseAsArrayOf(parseAsString).withDefault([]),
});

const TreatmentSearch = () => {
  const [searchData, setSearchData] = useState(null);
  const [searchParams, setSearchParams] = useQueryStates(createUrlParsers(), URL_CONFIG);

  const buildSearchUrlParams = (data) => {
    const urlParams = { view: VIEWS.RESULTS };
    
    Object.keys(data.selections).forEach((field) => {
      const values = data.selections[field] || [];
      if (values.length > 0) {
        urlParams[field] = values;
        
        const labels = data.selectionLabels[field] || [];
        const labelKey = `${field}_labels`;
        if (labels.length > 0) {
          urlParams[labelKey] = labels.map(item => 
            item.label || item.name || item.title || String(item.value || item.id || '')
          );
        }
      }
    });
    
    return urlParams;
  };

  const hasValidSearchParams = (params) => 
    Object.keys(params).some(key => 
      key !== 'view' && !key.endsWith('_labels') && params[key]?.length > 0
    );

  const reconstructSearchData = (params) => {
    const data = {
      selections: {},
      selectionLabels: {},
      completedSteps: 0,
    };
    
    Object.keys(params).forEach(field => {
      if (field !== 'view' && !field.endsWith('_labels') && params[field]?.length > 0) {
        data.selections[field] = params[field];
        data.completedSteps++;
        
        const labelKey = `${field}_labels`;
        const labelValues = params[labelKey] || [];
        const ids = params[field] || [];
        
        if (labelValues.length > 0) {
          data.selectionLabels[field] = ids.map((id, index) => ({
            value: id,
            label: labelValues[index] || id,
            id: id,
          }));
        }
      }
    });
    
    return data;
  };

  const handleSearch = useCallback((data) => {
    setSearchData(data);
    setSearchParams(buildSearchUrlParams(data));
  }, [setSearchParams]);

  const handleBackToSteps = useCallback(() => {
    setSearchParams({ view: VIEWS.STEPS });
  }, [setSearchParams]);

  useEffect(() => {
    const isResultsView = searchParams.view === VIEWS.RESULTS;
    const hasData = Boolean(searchData);
    
    if (isResultsView && !hasData) {
      if (hasValidSearchParams(searchParams)) {
        setSearchData(reconstructSearchData(searchParams));
      } else {
        setSearchParams({ view: VIEWS.STEPS });
      }
    }
  }, [searchParams, searchData, setSearchParams]);

  if (searchParams.view === VIEWS.RESULTS && searchData) {
    return (
      <TreatmentResults 
        searchData={searchData} 
        onBackToSteps={handleBackToSteps} 
      />
    );
  }

  return <TreatmentSteps onSearch={handleSearch} />;
};

export default TreatmentSearch;
